import {
    AfterContentInit,
    AfterViewChecked,
    Component,
    ContentChild,
    ElementRef,
    Input,
    KeyValueDiffer,
    KeyValueDiffers,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { SvgIcon } from '@valg-shared/utils/svg-icon';
import { DatepickerDay, DatepickerDays, DatepickerMonths, DatepickerYearOrMonth} from './datepicker';
import { AbstractControl, FormControlName } from '@angular/forms';
import { ConnectedPosition, ScrollStrategy, ScrollStrategyOptions } from "@angular/cdk/overlay";
import { InputType } from '../root/input-type';
import { TranslateService } from "@ngx-translate/core";
import { StringValidator } from '@valg-shared/utils/validators';
import { datePattern } from "../../../utils/patterns";

let DATEPICKER_ID = 0;

const YEAR_CHUNK = 12;
const DATE_PICKER_CLASS = `date-picker`;
const DATE_PICKER_CALENDAR_CLASS = `${DATE_PICKER_CLASS}__calendar`;
const CALENDAR_DATE_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__date`;
const CALENDAR_DATE_FOCUSED_CLASS = `${CALENDAR_DATE_CLASS}--focused`;
const CALENDAR_PREVIOUS_YEAR_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__previous-year`;
const CALENDAR_PREVIOUS_MONTH_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__previous-month`;
const CALENDAR_NEXT_YEAR_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__next-year`;
const CALENDAR_NEXT_MONTH_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__next-month`;
const CALENDAR_MONTH_SELECTION_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__month-selection`;
const CALENDAR_YEAR_SELECTION_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__year-selection`;
const CALENDAR_MONTH_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__month`;
const CALENDAR_MONTH_FOCUSED_CLASS = `${CALENDAR_MONTH_CLASS}--focused`;
const CALENDAR_YEAR_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__year`;
const CALENDAR_YEAR_FOCUSED_CLASS = `${CALENDAR_YEAR_CLASS}--focused`;
const CALENDAR_PREVIOUS_YEAR_CHUNK_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__previous-year-chunk`;
const CALENDAR_NEXT_YEAR_CHUNK_CLASS = `${DATE_PICKER_CALENDAR_CLASS}__next-year-chunk`;
const CALENDAR_DATE_FOCUSED = `.${CALENDAR_DATE_FOCUSED_CLASS}`;
const CALENDAR_PREVIOUS_YEAR = `.${CALENDAR_PREVIOUS_YEAR_CLASS}`;
const CALENDAR_PREVIOUS_MONTH = `.${CALENDAR_PREVIOUS_MONTH_CLASS}`;
const CALENDAR_NEXT_YEAR = `.${CALENDAR_NEXT_YEAR_CLASS}`;
const CALENDAR_NEXT_MONTH = `.${CALENDAR_NEXT_MONTH_CLASS}`;
const CALENDAR_YEAR_SELECTION = `.${CALENDAR_YEAR_SELECTION_CLASS}`;
const CALENDAR_MONTH_SELECTION = `.${CALENDAR_MONTH_SELECTION_CLASS}`;
const CALENDAR_PREVIOUS_YEAR_CHUNK = `.${CALENDAR_PREVIOUS_YEAR_CHUNK_CLASS}`;
const CALENDAR_NEXT_YEAR_CHUNK = `.${CALENDAR_NEXT_YEAR_CHUNK_CLASS}`;
const CALENDAR_MONTH_FOCUSED = `.${CALENDAR_MONTH_FOCUSED_CLASS}`;
const CALENDAR_YEAR_FOCUSED = `.${CALENDAR_YEAR_FOCUSED_CLASS}`;
const NOT_DISABLED_SELECTOR = ":not([disabled])";
const processFocusableSelectors = (...selectors: any) =>
selectors.map((query:any) => query + NOT_DISABLED_SELECTOR).join(", ");
const DATE_PICKER_FOCUSABLE = processFocusableSelectors(
  CALENDAR_PREVIOUS_YEAR,
  CALENDAR_PREVIOUS_MONTH,
  CALENDAR_YEAR_SELECTION,
  CALENDAR_MONTH_SELECTION,
  CALENDAR_NEXT_YEAR,
  CALENDAR_NEXT_MONTH,
  CALENDAR_DATE_FOCUSED
);

const MONTH_PICKER_FOCUSABLE = processFocusableSelectors(
  CALENDAR_MONTH_FOCUSED
);

const YEAR_PICKER_FOCUSABLE = processFocusableSelectors(
  CALENDAR_PREVIOUS_YEAR_CHUNK,
  CALENDAR_NEXT_YEAR_CHUNK,
  CALENDAR_YEAR_FOCUSED
);

@Component({
  selector: 'valg-datepicker',
  templateUrl: './datepicker.component.html'
})
export class DatepickerComponent implements AfterContentInit, AfterViewChecked {
  @Input() preferCurrentCenturyForTwoDigitYear = false;
  @Input() minDate: Date = new Date("0000-01-01");
  @Input() maxDate!: Date;
  @Input() defaultDate: Date = this.today();
  @Input() isBirthdatePicker = false;
  @Input() preSelectToday = false;
  @ContentChild(InputType, { static: false }) public inputElement!: InputType;

  datepickerOpen: ElementRef | null = null;
  @ViewChild('datepicker') set datepicker(datepicker: ElementRef|null) {
    if(!datepicker) return;
    this.datepickerOpen = datepicker;
  }
    
  differ: KeyValueDiffer<string, any>;
  
  public calendarData: Array<DatepickerDay[]> = [];
  public calendarYears: DatepickerYearOrMonth[][] = [];
  public calendarMonths:  DatepickerYearOrMonth[][] = [];
  public formControl!: AbstractControl | null;
  public showCalendarYearPicker: boolean = false;
  public showCalendarMonthPicker: boolean = false;
  public datepickerStatus: string = "";
  public datepickerStatusParameters: { year?: number, month?: string, yearFromChunk?: number, yearToChunk?:  number} = {};
  public isDatepickerOpen : boolean = false;
  public focusedMonth = "";
  public previousYear : number | null = null;
  public onChange: any = () => {}
  public onTouched: any = () => {}
  public disabled = false;
  public id = `valg-datepicker-${DATEPICKER_ID++}`;
  public weekdays = DatepickerDays;

  private setFocus = false;
  private calendarFocusedMonth: number = 0;

  protected _focusedDate : Date = new Date();
  
  readonly SvgIcon = SvgIcon;

  set calendarFocusedDate(date: Date){
    this._focusedDate = date;
    this.calendarFocusedMonth = date.getMonth();
  }
  get calendarFocusedDate(){
    return this._focusedDate;
  }

  isMenuOverlayTriggered = false;
  scrollStrategy: ScrollStrategy;

  private belowPosition = {
    originX: 'end',
    originY: 'bottom',
    overlayX: 'end',
    overlayY: 'top',
    panelClass: 'valg-date-picker-overlay__below',
    offsetY: 16,
  } as ConnectedPosition;

  private abovePosition = {
    originX: 'end',
    originY: 'top',
    overlayX: 'end',
    overlayY: 'bottom',
    panelClass: 'valg-date-picker-overlay__above',
    offsetY: -16,
  } as ConnectedPosition;

  overlayConnectedPositions: ConnectedPosition[] = [
      this.belowPosition,
      this.abovePosition
  ];

  constructor(
    protected renderer: Renderer2,
    protected readonly _sso: ScrollStrategyOptions,
    private differs: KeyValueDiffers,
    protected translate: TranslateService
    ) {
      this.differ = this.differs.find({}).create();
      this.scrollStrategy = _sso.reposition();
  }  

  public ngAfterContentInit (): void {
    if(this.preSelectToday){
      this.selectDate(new Date());
    }
    if(this.inputElement){
      this.formControl = this.inputElement.getFormControl();
      this.formControl.addValidators([ StringValidator.isDateFormat ]);
      this.disabled = this.formControl.disabled;

      if(this.inputElement.errorKey === undefined){
        this.inputElement.errorKey = "standard_input.date_input.validation_error"
      }

      if(this.inputElement.getAttribute('readonly') !== null){
        this.disabled = true;
      }

      if (this.formControl instanceof FormControlName) {
        this.formControl = this.formControl.control as AbstractControl;
      }

      this.inputElement.focusOut.subscribe({
        next: () => {
          const value = this.inputElement.getFormControl().value;
          if(value && value.match(datePattern)){
            let parsedDate = this.parseDateString(value, true);      
            if(parsedDate){
              this.setCalendarValue(parsedDate);
            }
          }
        }
    })
    }
  }

  ngAfterViewChecked(){
    if(this.showCalendarYearPicker){
      this.setFocusOnYear()
    } else if(this.showCalendarMonthPicker){
      this.setFocusOnMonth();
    } else{
      this.setFocusOnNewDate();
    }
    if(this.formControl){
      this.disabled = this.formControl.disabled
    }
  }

  setFocusOnYear(){
    if(this.setFocus){
      let focusedYear = this.datepickerOpen?.nativeElement.querySelector(CALENDAR_YEAR_FOCUSED);
      if(focusedYear){
        if(this.calendarFocusedDate.getFullYear() === parseInt(focusedYear.getAttribute('data-value'))){
          this.setFocus = false;
          focusedYear.focus();
        }
      }
    }
  }

  setFocusOnMonth(){
    if(this.setFocus){
      let focusedMonth = this.datepickerOpen?.nativeElement.querySelector(CALENDAR_MONTH_FOCUSED);
      if(focusedMonth){
        if(this.calendarFocusedMonth === parseInt(focusedMonth.getAttribute('data-value'))){
          this.setFocus = false;
          focusedMonth.focus();
        }
      }
    }
  }
  
  setFocusOnNewDate(){
    if(this.setFocus){
      let newDateEl = this.datepickerOpen?.nativeElement.querySelector(CALENDAR_DATE_FOCUSED);
      if(newDateEl){
        let date = new Date(newDateEl.getAttribute('data-value'));
        date.setHours(0,0,0);
        this.calendarFocusedDate.setHours(0,0,0);
        if(this.calendarFocusedDate.getTime() === date.getTime()){
          newDateEl.focus();
          this.setFocus = false;
        }
      }
    }
  }

  setFocusOnInput(){
    this.inputElement.getElementRef()?.nativeElement.focus();
  }

  selectedDate(): Date {
    const value = this.inputElement.getFormControl().value;
    if(value && value?.length > 0){
      let parsedDate = this.parseDateString(value, true);      
      if(parsedDate){
        return parsedDate;
      }
    }
    return this.defaultDate;
  }

  isDateFocused(date: Date): boolean{
    date.setHours(0,0,0);
    this.calendarFocusedDate.setHours(0,0,0);
    return this.calendarFocusedDate.getTime() === date.getTime();
  }
  isDateInCurrentMonth(date: Date): boolean{
    return this.calendarFocusedDate.getMonth() === date.getMonth();
  }
  
  isMonthSelected(month: number){
    const selectedMonth = this.selectedDate().getMonth();
    return selectedMonth === month;
  }
  isMonthFocused(month: number): boolean{
    return this.calendarFocusedMonth === month;
  }
  isYearFocused(year: number): boolean{
    const focusedMonth = this.calendarFocusedDate.getFullYear();
    return focusedMonth === year;
  }


  private keepDateWithinMonth (dateToCheck:Date, month:number) : Date{
    if (month !== dateToCheck.getMonth()) {
      dateToCheck.setDate(0);
    }

    return dateToCheck;
  }

  private setDate(year:number, month:number, date:number) : Date {
    const newDate = new Date(0);
    newDate.setFullYear(year, month, date);
    return newDate;
  }

  private today() : Date {
    const newDate = new Date();
    const day = newDate.getDate();
    const month = newDate.getMonth();
    const year = newDate.getFullYear();
    return this.setDate(year, month, day);
  }

  private startOfMonth(date: Date) : Date {
    const newDate = new Date(0);
    newDate.setFullYear(date.getFullYear(), date.getMonth(), 1);
    return newDate;
  }

  private lastDayOfMonth(date: Date) : Date {
    const newDate = new Date(0);
    newDate.setFullYear(date.getFullYear(), date.getMonth() + 1, 0);
    return newDate;
  }

  private addDays(_date: Date, numDays: number) : Date {
    const newDate = new Date(_date.getTime());
    newDate.setDate(newDate.getDate() + numDays);
    return newDate;
  }

  private subDays(_date:Date, numDays:number) : Date {
    return this.addDays(_date, -numDays);
  } 
  
  private addWeeks(_date: Date, numWeeks: number) : Date {
    return this.addDays(_date, numWeeks * 7);
  }

  private subWeeks(_date: Date, numWeeks: number) : Date{
    return this.addWeeks(_date, -numWeeks);
  }

  private startOfWeek(_date: Date) : Date {
    let dayOfWeek = _date.getDay()-1;
    if(dayOfWeek === -1){
      dayOfWeek = 6;
    }
    return this.subDays(_date, dayOfWeek);
  }

  private endOfWeek(_date: Date) : Date {
    const dayOfWeek = _date.getDay();
    return this.addDays(_date, 7 - dayOfWeek);
  }

  private addMonths(_date: Date, numMonths: number) : Date {
    const newDate = new Date(_date.getTime());

    const dateMonth = (newDate.getMonth() + 12 + numMonths) % 12;
    newDate.setMonth(newDate.getMonth() + numMonths);
    this.keepDateWithinMonth(newDate, dateMonth);

    return newDate;
  }

  private subMonths(_date: Date, numMonths: number) : Date {
    return this.addMonths(_date, -numMonths);
  } 

  private addYears(_date: Date, numYears: number) : Date {
    return this.addMonths(_date, numYears * 12);
  }
  
  private subYears(_date: Date, numYears: number) : Date {
    return this.addYears(_date, -numYears);
  }

  private setMonth(_date: Date, month: number) : Date {
    const newDate = new Date(_date.getTime());

    newDate.setMonth(month);
    this.keepDateWithinMonth(newDate, month);

    return newDate;
  }

  private setYear(_date: Date, year: number) : Date {
    const newDate = new Date(_date.getTime());

    const month = newDate.getMonth();
    newDate.setFullYear(year);
    this.keepDateWithinMonth(newDate, month);

    return newDate;
  }

  private isSameYear(dateA: Date, dateB: Date) : boolean {
    return dateA && dateB && dateA.getFullYear() === dateB.getFullYear();
  }

  private isSameMonth(dateA: Date, dateB: Date) : boolean {
    return this.isSameYear(dateA, dateB) && dateA.getMonth() === dateB.getMonth();
  }

  private isSameDay(dateA: Date, dateB: Date) : boolean {
    return this.isSameMonth(dateA, dateB) && dateA.getDate() === dateB.getDate();
  }

  private keepDateBetweenMinAndMax(date: Date, minDate: Date, maxDate: Date) : Date {
    let newDate = date;
  
    if (date < minDate) {
      newDate = minDate;
    } else if (maxDate && date > maxDate) {
      newDate = maxDate;
    }
  
    return newDate;
  }

  private isDateWithinMinAndMax(date: Date, minDate: Date, maxDate: Date) : boolean {
    return date >= minDate && (!maxDate || date <= maxDate)
  }
  private isDatesMonthOutsideMinOrMax(date: Date, minDate: Date, maxDate: Date) : boolean {
    return (
      this.lastDayOfMonth(date) < minDate || (maxDate && this.startOfMonth(date) > maxDate)
    );
  }

  private isDatesYearOutsideMinOrMax (date: Date, minDate: Date, maxDate: Date) : boolean {
    return (
      this.lastDayOfMonth(this.setMonth(date, 11)) < minDate ||
      (maxDate && this.startOfMonth(this.setMonth(date, 0)) > maxDate)
    );
  }

  private getCentury (year: number) {
    return year - (year % 100);
  }

  private parseDateString(
    dateString: string,
    adjustDate: boolean = false
  ) : Date | undefined {
    let date;
    let month;
    let day;
    let year;
    let parsed_day;
    let parsed_month;
    let parsed_year;
  
    if (dateString) {
      let monthStr, dayStr, yearStr;
      let numbers  = dateString.match(/\d+/g);
      if(numbers !== null){
        if(numbers.length === 3){
          dayStr = numbers[0];
          monthStr = numbers[1];
          yearStr = numbers[2];
        } else if(numbers.length === 1){
          if(dateString.length === 8 || dateString.length === 6){
            dayStr = dateString.substring(0, 2);
            monthStr = dateString.substring(2, 4);
            yearStr = dateString.slice(4);
          }
        } 
      }
        
      if (yearStr && dayStr && monthStr) {
        parsed_day = parseInt(dayStr, 10);
        parsed_month = parseInt(monthStr, 10);
        parsed_year = parseInt(yearStr, 10);
        if (!Number.isNaN(parsed_day) && !Number.isNaN(parsed_month) && !Number.isNaN(parsed_year)) {
          day = parsed_day
          month = parsed_month
          year = parsed_year;
          if (adjustDate) {
            if (yearStr.length == 2) {
              /*
              Format yy to yyyy
              Examples of inputs and results
              121222 in the current date 13/12/2022 => 2022
              131222 in the current date 13/12/2022 => 2022
              141222 in the current date 13/12/2022 => 1922
              */
              const currentDate = this.today().getDate();
              const currentMonth = this.today().getMonth() + 1;
              const currentYear = this.today().getFullYear();
              const yearStub = currentYear % 100;
              if (yearStub < year ||
                (currentMonth < month && yearStub <= year) ||
                (currentDate < day && currentMonth == month && yearStub <= year)) {
                const previousYear = this.preferCurrentCenturyForTwoDigitYear ? currentYear : currentYear - 100;
                const previousCentury = this.getCentury(previousYear);
                year = previousCentury + parsed_year;
              }
              else {
                const currentCentury =  this.getCentury(currentYear)
                year = currentCentury + parsed_year;
              }
            }
          }
        }
      }

      if (month != null) {
        if (adjustDate) {
          month = Math.max(1, month);
          month = Math.min(12, month);
        }
      }
  
      if (month != null && day != null && year != null) {
        if (adjustDate) {
          const lastDayOfTheMonth = this.setDate(year, month, 0).getDate();
          day = Math.max(1, day);
          day = Math.min(lastDayOfTheMonth, day);
        }
      }
      
      if (month && day && year != null) {
        date = this.setDate(year, month - 1, day);
      }
    }
    return date;
  }
    
  private formatDate (date: Date) : string {
    const padZeros = (value: number, length: number) => {
      return `0000${value}`.slice(-length);
    };

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();

    return [padZeros(day, 2), padZeros(month, 2), padZeros(year, 4)].join("-");
  }

  private buildCalendarMonth(days: DatepickerDay[], rowSize: number): Array<DatepickerDay[]> {
    const month: DatepickerDay[][] = [];

    let i = 0;
    while (i < days.length) {
      let week: DatepickerDay[] = [];
      while (i < days.length && week.length < rowSize) {
        week.push(days[i]);
        i += 1;
      }
      month.push(week);
    }

    return month;
  }

  private buildMonthsOrYears(units: DatepickerYearOrMonth[], rowSize: number): DatepickerYearOrMonth[][] {
    const grid: DatepickerYearOrMonth[][] = [];

    let i = 0;
    while (i < units.length) {
      let row: DatepickerYearOrMonth[] = [];
      while (i < units.length && row.length < rowSize) {
        row.push(units[i]);
        i += 1;
      }
      grid.push(row);
    }

    return grid;
  }

  private changeElementValue (value: string) {
    var form = this.inputElement.getFormControl();
    var oldValue = form.value;
    if(value != oldValue){
      form.setValue(value);
      form.markAsDirty();   
    }               
  }

  private setCalendarValue(date: Date, setInput = true) : void {
      const formattedDate = this.formatDate(date);
      
      this.calendarFocusedDate = date;
      if(setInput){
        this.changeElementValue(formattedDate);
      }
  }

  private generateDay(dateToRender: Date, prevMonth: Date, nextMonth: Date) : DatepickerDay {
    const day = dateToRender.getDate();
    const month = dateToRender.getMonth();
    const year = dateToRender.getFullYear();
    let dayOfWeek = dateToRender.getDay() - 1;
    if(dayOfWeek === -1){
      dayOfWeek = 6;
    }

    let isDisabled = !this.isDateWithinMinAndMax(dateToRender, this.minDate, this.maxDate);
    let isFocused = false;
    const isSelected = this.selectedDate() !== null ? this.isSameDay(dateToRender, this.selectedDate()) : false;

    if (this.isSameMonth(dateToRender, prevMonth)) {
      isDisabled = true;
    }

    if (this.isSameMonth(dateToRender, nextMonth)) {
      isDisabled = true;
    }

    if(this.isDateFocused(dateToRender)){
      isFocused = true;
    }

    const monthKey = DatepickerMonths[month];
    const dayKey = DatepickerDays[dayOfWeek];

    return {
      day: dateToRender.getDate(),
      month: dateToRender.getMonth(),
      year: dateToRender.getFullYear(),
      value: dateToRender,
      label: { 'weekday': this.translate.instant(dayKey), 'day': day, 'month': this.translate.instant(monthKey).toLowerCase(), 'year': year },
      selected: isSelected,
      disabled: isDisabled,
      focused: isFocused,
    };
  }

  private renderCalendar(_dateToDisplay: Date) : void {
    const todaysDate = this.today();
    let dateToDisplay = _dateToDisplay || todaysDate;
    this.calendarFocusedDate = dateToDisplay;
    this.focusedMonth = DatepickerMonths[dateToDisplay.getMonth()];

    const calendarWasHidden = !this.isDatepickerOpen;

    const focusedDate = this.addDays(dateToDisplay, 0);
    const focusedMonth = dateToDisplay.getMonth();
    const focusedYear = dateToDisplay.getFullYear();

    const prevMonth = this.subMonths(dateToDisplay, 1);
    const nextMonth = this.addMonths(dateToDisplay, 1);

    const firstOfMonth = this.startOfMonth(dateToDisplay);

    const monthLabel = DatepickerMonths[focusedMonth];

    dateToDisplay = this.startOfWeek(firstOfMonth);

    const days: DatepickerDay[] = [];

    while (
      days.length < 28 ||
      dateToDisplay.getMonth() === focusedMonth ||
      days.length % 7 !== 0
    ) {
      days.push(this.generateDay(dateToDisplay, prevMonth, nextMonth));
      dateToDisplay = this.addDays(dateToDisplay, 1);    
    }
    this.calendarData = this.buildCalendarMonth(days, 7);

    if (this.isSameDay(this.selectedDate(), focusedDate)) {
      this.setStatus('datepicker.selected_date');
    }

    if (calendarWasHidden) {
      this.setStatus('datepicker.guideline');
    } else {
      this.translate.get(monthLabel).subscribe((res: string) => {
        this.setStatus('datepicker.month_year', { year: focusedYear, month: res });
      });
    }
  }

  private setStatus(status: string, parameters: { year?: number, month?: string, yearFromChunk?: number, yearToChunk?:  number} | null = null) : void{
    if(parameters !== null){
      this.datepickerStatusParameters = parameters;
    } else{
      this.datepickerStatusParameters = {};
    }

    this.datepickerStatus = status;
  }

  public displayPreviousYear() : void {
    let date = this.subYears(this.calendarFocusedDate, 1);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.renderCalendar(date);
  }

  public displayPreviousMonth() : void {
    let date = this.subMonths(this.calendarFocusedDate, 1);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.renderCalendar(date);
  }

  public displayNextMonth() : void {
    let date = this.addMonths(this.calendarFocusedDate, 1);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.renderCalendar(date);
  }

  public displayNextYear() : void {
    let date = this.addYears(this.calendarFocusedDate, 1);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.renderCalendar(date);
  }
  
  public hideCalendar() : void {
    this.isMenuOverlayTriggered = false;
    this.isDatepickerOpen = false;
    this.showCalendarYearPicker = false;
    this.showCalendarMonthPicker = false;
    this.setStatus('');
    this.inputElement?.focusOut.emit();
    this.setFocusOnInput();
  }

  public selectDate(calendarDate: Date) : void {
    this.setCalendarValue(calendarDate);
    this.hideCalendar();
  }

  toggleCalendar () : void {
    this.previousYear = null;
    if (!this.isDatepickerOpen) {
      const dateToDisplay = this.keepDateBetweenMinAndMax(
        this.selectedDate(),
        this.minDate,
        this.maxDate
      );

      this.setCalendarValue(this.selectedDate(), false);
      
      this.renderCalendar(dateToDisplay);
      
      this.isMenuOverlayTriggered = true;
      this.isDatepickerOpen = true;
      this.setFocus = true;
    } else {
      this.hideCalendar();
    }
  }

  public displayMonthSelection (monthToDisplay: number | null = null) : void {
    this.showCalendarMonthPicker = true;
    this.setFocus = true;
    if(this.calendarFocusedMonth === null){
      this.calendarFocusedMonth = this.calendarFocusedDate.getMonth();
    }
    if(monthToDisplay !== null){
      this.calendarFocusedMonth = monthToDisplay;
    }
    const selectedMonth = this.calendarFocusedDate.getMonth();

    let months: DatepickerYearOrMonth[] = DatepickerMonths.map((month, index) => {
      const monthToCheck = this.setMonth(this.calendarFocusedDate, index);

      const isDisabled = this.isDatesMonthOutsideMinOrMax(
        monthToCheck,
        this.minDate,
        this.maxDate
      );

      const isSelected = index === selectedMonth;
      return {
        value: index,
        label: month,
        selected: isSelected,
        disabled: isDisabled
      };
    });
    this.calendarMonths = this.buildMonthsOrYears(months, 3);
    
    this.setStatus('datepicker.select_month');
  }

  public selectMonth (month: number) {
    let date = this.setMonth(this.calendarFocusedDate, month);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.calendarFocusedDate = date;
    this.renderCalendar(date);
    this.showCalendarMonthPicker = false;
    this.setFocus = true;
  }

  public displayYearSelection (yearToDisplay: number | null = null, focus: boolean = true) {
    if(focus){
      this.setFocus = true;
    }
    
    if(yearToDisplay !== null){
      this.calendarFocusedDate = this.setYear(this.calendarFocusedDate, yearToDisplay);
    }
    const selectedYear = this.calendarFocusedDate.getFullYear();
    const focusedYear = yearToDisplay == null ? selectedYear : yearToDisplay;

    let yearToChunk = focusedYear;
    yearToChunk -= yearToChunk % YEAR_CHUNK;
    yearToChunk = Math.max(0, yearToChunk);

    let years : DatepickerYearOrMonth[] = [];
    let yearIndex = yearToChunk;
    while (years.length < YEAR_CHUNK) {
      const isDisabled = this.isDatesYearOutsideMinOrMax(
        this.setYear(this.selectedDate(), yearIndex),
        this.minDate,
        this.maxDate
      );
      const isSelected = yearIndex === selectedYear;

      years.push({
        value:yearIndex,
        selected: isSelected,
        disabled: isDisabled,
        label: ''
      });
      yearIndex += 1;
    }

    this.calendarYears = this.buildMonthsOrYears(years, 3);

    this.setStatus('datepicker.select_year', { yearFromChunk: yearToChunk, yearToChunk:  yearToChunk + YEAR_CHUNK - 1 });
    
    this.showCalendarYearPicker = true;
  }

  public displayPreviousYearChunk() {
    const selectedYear = this.selectedDate().getFullYear();
    const focusedYear = this.previousYear == null ? selectedYear : this.previousYear;

    let adjustedYear = focusedYear - YEAR_CHUNK;
    adjustedYear = Math.max(0, adjustedYear);
    this.previousYear = adjustedYear;

    const date = this.setYear(this.selectedDate(), adjustedYear);
    const cappedDate = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.displayYearSelection(
      cappedDate.getFullYear(),
      false
    );
  }

  public isPreviousYearChunkDisabled(){
    const focusedYear = this.calendarFocusedDate.getFullYear();

    let yearToChunk = focusedYear;
    yearToChunk -= yearToChunk % YEAR_CHUNK;
    yearToChunk = Math.max(0, yearToChunk);

    return this.isDatesYearOutsideMinOrMax(
      this.setYear(this.selectedDate(), yearToChunk - 1),
      this.minDate,
      this.maxDate
    );
  }

  
  public isNextYearChunkDisabled(){
    const focusedYear = this.calendarFocusedDate.getFullYear();

    let yearToChunk = focusedYear;
    yearToChunk -= yearToChunk % YEAR_CHUNK;
    yearToChunk = Math.max(0, yearToChunk);

    return this.isDatesYearOutsideMinOrMax(
      this.setYear(this.selectedDate(), yearToChunk + YEAR_CHUNK),
      this.minDate,
      this.maxDate
    );
  }

  public isPreviousButtonsDisabled(){
    return this.isSameMonth(this.calendarFocusedDate, this.minDate);
    
  }
  public isNextButtonsDisabled(){
    return this.isSameMonth(this.calendarFocusedDate, this.maxDate)
  }

  public displayNextYearChunk() {
    const selectedYear = this.selectedDate().getFullYear();
    const focusedYear = this.previousYear == null ? selectedYear : this.previousYear;

    let adjustedYear = focusedYear + YEAR_CHUNK;
    adjustedYear = Math.max(0, adjustedYear);
    this.previousYear = adjustedYear

    const date = this.setYear(this.selectedDate(), adjustedYear);
    const cappedDate = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.displayYearSelection(
      cappedDate.getFullYear(),
      false
    );
  }

  public selectYear(year: number) {
    let date = this.setYear(this.selectedDate(), year);
    date = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
    this.renderCalendar(date);
    this.showCalendarYearPicker = false;
    this.setFocus = true;
  }

  public handleEscapeFromCalendar(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.hideCalendar();
    this.setFocusOnInput();
  }

  private adjustCalendar (adjustDateFn: Function) : Function {
    return () => {
      const date = adjustDateFn(this.calendarFocusedDate);

      const cappedDate = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
      if(this.calendarFocusedDate.getTime() !== cappedDate.getTime()){
        this.renderCalendar(cappedDate);
        this.setFocus = true;
      }
    };
  }

  private handleUpFromDate = this.adjustCalendar((date: Date) => this.subWeeks(date, 1));
  private handleDownFromDate = this.adjustCalendar((date: Date) => this.addWeeks(date, 1));
  private handleLeftFromDate = this.adjustCalendar((date: Date) => this.subDays(date, 1));
  private handleRightFromDate = this.adjustCalendar((date: Date) => this.addDays(date, 1));
  private handleHomeFromDate = this.adjustCalendar((date: Date) => this.startOfWeek(date));
  private handleEndFromDate = this.adjustCalendar((date: Date) => this.endOfWeek(date));
  private handlePageDownFromDate = this.adjustCalendar((date: Date) => this.addMonths(date, 1));
  private handlePageUpFromDate = this.adjustCalendar((date: Date) => this.subMonths(date, 1));
  private handleShiftPageDownFromDate = this.adjustCalendar((date: Date) => this.addYears(date, 1));
  private handleShiftPageUpFromDate = this.adjustCalendar((date: Date) => this.subYears(date, 1));

  private adjustMonthSelectionScreen (adjustMonthFn: Function) : Function {
    return () => {
      let adjustedMonth = adjustMonthFn(this.calendarFocusedMonth);
      adjustedMonth = Math.max(0, Math.min(11, adjustedMonth));

      const date = this.setMonth(this.calendarFocusedDate, adjustedMonth);
      const cappedDate = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
      
      if (this.calendarFocusedMonth !== adjustedMonth) {
        this.displayMonthSelection(
          cappedDate.getMonth()
        );
      }
    }
  }

  private handleUpFromMonth = this.adjustMonthSelectionScreen((month:number) => month - 3);
  private handleDownFromMonth = this.adjustMonthSelectionScreen((month:number) => month + 3);
  private handleLeftFromMonth = this.adjustMonthSelectionScreen((month:number) => month - 1);
  private handleRightFromMonth = this.adjustMonthSelectionScreen((month:number) => month + 1);
  private handleHomeFromMonth = this.adjustMonthSelectionScreen((month:number) => month - (month % 3));
  private handleEndFromMonth = this.adjustMonthSelectionScreen((month:number) => month + 2 - (month % 3));
  private handlePageDownFromMonth = this.adjustMonthSelectionScreen(() => 11);
  private handlePageUpFromMonth = this.adjustMonthSelectionScreen(() => 0);

  private adjustYearSelectionScreen (adjustYearFn: Function) : Function {
    return () => {
      let adjustedYear = adjustYearFn(this.calendarFocusedDate.getFullYear());
      adjustedYear = Math.max(0, adjustedYear);

      const date = this.setYear(this.calendarFocusedDate, adjustedYear);
      const cappedDate = this.keepDateBetweenMinAndMax(date, this.minDate, this.maxDate);
      if (!this.isSameYear(this.calendarFocusedDate, cappedDate)) {
        this.displayYearSelection(cappedDate.getFullYear());
      }
    };
  }

  private handleUpFromYear = this.adjustYearSelectionScreen((year: number) => year - 3);
  private handleDownFromYear = this.adjustYearSelectionScreen((year: number) => year + 3);
  private handleLeftFromYear = this.adjustYearSelectionScreen((year: number) => year - 1);
  private handleRightFromYear = this.adjustYearSelectionScreen((year: number) => year + 1);
  private handleHomeFromYear = this.adjustYearSelectionScreen((year: number) => year - (year % 3));
  private handleEndFromYear = this.adjustYearSelectionScreen((year: number) => year + 2 - (year % 3));
  private handlePageUpFromYear = this.adjustYearSelectionScreen((year: number) => year - YEAR_CHUNK);
  private handlePageDownFromYear = this.adjustYearSelectionScreen((year: number) => year + YEAR_CHUNK);

  private tabHandler (focusable: string) : {tabAhead: Function, tabBack: Function} {
    const getFocusableContext = (_el: any) => {
      const focusableElements : any[] = this.datepickerOpen?.nativeElement.querySelectorAll(focusable);

      const firstTabIndex = 0;
      const lastTabIndex = focusableElements.length - 1;
      const firstTabStop = focusableElements[firstTabIndex];
      const lastTabStop = focusableElements[lastTabIndex];
      const focusIndex = Array.prototype.indexOf.call(focusableElements, document.activeElement);

      const isLastTab = focusIndex === lastTabIndex;
      const isFirstTab = focusIndex === firstTabIndex;
      const isNotFound = focusIndex === -1;

      return {
        focusableElements,
        isNotFound,
        firstTabStop,
        isFirstTab,
        lastTabStop,
        isLastTab,
      };
    };

    return {
      tabAhead: (event: any) => {
        const { firstTabStop, isLastTab, isNotFound } = getFocusableContext(
          event.target
        );

        if (isLastTab || isNotFound) {
          event.preventDefault();
          firstTabStop.focus();
        }
      },
      tabBack : (event: any) => {
        const { lastTabStop, isFirstTab, isNotFound } = getFocusableContext(
          event.target
        );

        if (isFirstTab || isNotFound) {
          event.preventDefault();
          lastTabStop.focus();
        }
      }
    };
  }

  public datePickerTabEventHandler = this.tabHandler(DATE_PICKER_FOCUSABLE);
  public monthPickerTabEventHandler = this.tabHandler(MONTH_PICKER_FOCUSABLE);
  public yearPickerTabEventHandler = this.tabHandler(YEAR_PICKER_FOCUSABLE);

  public onDateKeydown(event: any, date: Date): void{
    let handled = false;
    if (event.key === "ArrowUp") {
      this.handleUpFromDate(date);
      handled = true;
    } else if (event.key === "ArrowDown") {
      this.handleDownFromDate(date);
      handled = true;
    } else if (event.key === "ArrowLeft") {
      this.handleLeftFromDate(date);
      handled = true;
    } else if (event.key === "ArrowRight") {
      this.handleRightFromDate(date);
      handled = true;
    } else if (event.key === "Home") {
      this.handleHomeFromDate(date);
      handled = true;
    } else if (event.key === "End") {
      this.handleEndFromDate(date);
      handled = true;
    } else if (event.key === "PageUp" && event.shiftKey === false) {
      this.handlePageUpFromDate(date);
      handled = true;
    } else if (event.key === "PageDown" && event.shiftKey === false) {
      this.handlePageDownFromDate(date);
      handled = true;
    } else if (event.key === "PageDown") {
      this.handleShiftPageDownFromDate(date);
      handled = true;
    } else if (event.key === "PageUp") {
      this.handleShiftPageUpFromDate(date);
      handled = true;
    }

    if(handled){
      event.preventDefault();
    }
  }

  public onMonthKeydown(event: any, month: number): void{
    let handled = false;
    if (event.key === "ArrowUp") {
      this.handleUpFromMonth(month);
      handled = true;
    } else if (event.key === "ArrowDown") {
      this.handleDownFromMonth(month);
      handled = true;
    } else if (event.key === "ArrowLeft") {
      this.handleLeftFromMonth(month);
      handled = true;
    } else if (event.key === "ArrowRight") {
      this.handleRightFromMonth(month);
      handled = true;
    } else if (event.key === "Home") {
      this.handleHomeFromMonth(month);
      handled = true;
    } else if (event.key === "End") {
      this.handleEndFromMonth(month);
      handled = true;
    } else if (event.key === "PageUp") {
      this.handlePageUpFromMonth(month);
      handled = true;
    } else if (event.key === "PageDown") {
      this.handlePageDownFromMonth(month);
      handled = true;
    }
    
    if(handled){
      event.preventDefault();
    }
  }

  public onYearKeydown(event: any, year: number): void{
    let handled = false;
    if (event.key === "ArrowUp") {
      this.handleUpFromYear(year);
      handled = true;
    } else if (event.key === "ArrowDown") {
      this.handleDownFromYear(year);
      handled = true;
    } else if (event.key === "ArrowLeft") {
      this.handleLeftFromYear(year);
      handled = true;
    } else if (event.key === "ArrowRight") {
      this.handleRightFromYear(year);
      handled = true;
    } else if (event.key === "Home") {
      this.handleHomeFromYear(year);
      handled = true;
    } else if (event.key === "End") {
      this.handleEndFromYear(year);
      handled = true;
    } else if (event.key === "PageUp") {
      this.handlePageUpFromYear(year);
      handled = true;
    } else if (event.key === "PageDown") {
      this.handlePageDownFromYear(year);
      handled = true;
    }
    
    if(handled){
      event.preventDefault();
    }
  }
}